import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../u2000Core/axios/U2000UseAxiosAuthenticated";
import { Outlet } from "react-router-dom";
import i18next from "i18next";
import { useU2000InitLUHandlers } from "../u2000Core/hooks/handler/U2000UseInitLUHandlers";
import { U2000HandleApiError, type U2000ApiErrorResult } from "../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../u2000Core/context/toast/U2000ToastContext";
import { doInitLU, luHandleAppiError } from "./U3148Service";
import { U3148ContextProvider } from "./U3148Context";
import { useU2000LogicalUnitContext } from "../u2000Core/context/logicalUnit/U2000LogicalUnitContext";

// Namespace constant and roles definition
export const NS = "u3148";

// Definition des rôles pour l'unité logique U3148
export const U3148ROLES = {
  READ: "U3148:READ",
  EDIT: "U3148:EDIT",
  AUDITS: "U3148:AUDITS",
  ROLES: "U3148:ROLES",
  USERS: "U3148:USERS",
} as const;
export type U3148ROLES = (typeof U3148ROLES)[keyof typeof U3148ROLES];

// Definition of the initialization parameters for the logical unit U3148
export interface U3148InitLU {
  launchSearch: boolean;
  hideSearch: boolean;
  nbRecordPerPage: number;
  currentOrganizationId: string;
}

// Main component for the U3148 logical unit
const U3148: React.FC = () => {
  const { i18n } = useTranslation();
  const [i18nReady, setI18nReady] = useState<boolean>(false);
  const { t } = useTranslation();
  const toast = useToast();
  const [initLUReady, setInitLUReady] = useState<boolean>(false);
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const [initLUData, setInitLUData] = useState<U3148InitLU>({
    launchSearch: false,
    hideSearch: true,
    nbRecordPerPage: 50,
    currentOrganizationId: "",
  });

  // charge fichiers de traduction
  useEffect(() => {
    i18next.loadNamespaces(["u3148"], (err) => {
      if (err) return console.error("Error loading namespace:", err);
      setI18nReady(true);
    });
  }, [i18n]);

  // Gestion de l'initialisation de l'unité logique
  const handleInitLU = useU2000InitLUHandlers<U3148InitLU>({
    doInitLUFn: async () => {
      return await doInitLU(u2000AxiosAuthenticated);
    },
    onSuccess: (initLU: U3148InitLU) => {
      setInitLUData(initLU);
      setInitLUReady(true);
    },
    onError: (error: any) => {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError) as U2000ApiErrorResult;
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
      setInitLUReady(true);
    },
  });

  // Initialisation de l'unité logique au montage du composant et apres quelques changements de langue
  useEffect(() => {
    handleInitLU.getInitLU();
  }, [t]);

  // Set LU title and breadcrumb
  const { setLogicalUnitTitle, setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  useEffect(() => {
    if (!i18nReady || !initLUReady) return;
    const title = t("title", { ns: NS });
    setLogicalUnitTitle(title);
    return () => {
      setLogicalUnitTitle(null);
      setLogicalUnitBreadcrumb([]);
    };
  }, [i18nReady, initLUReady, t, setLogicalUnitTitle, setLogicalUnitBreadcrumb]);

  if (!i18nReady || !initLUReady) return null;

  return (
    <U3148ContextProvider initLU={initLUData}>
      <Outlet />
    </U3148ContextProvider>
  );
};

export default U3148;
